import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, Leaf, Euro, ArrowRight, Star, CheckCircle2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS, cn } from '@/lib/index';
import { services, testimonials, companyInfo } from '@/data/index';
import { ServiceCard, TestimonialCard, BeforeAfterCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
const Home: React.FC = () => {
  const mainServices = services.slice(0, 3);
  const featuredTestimonials = testimonials.slice(0, 3);
  const benefits = [{
    icon: <ShieldCheck className="w-6 h-6 text-primary" />,
    title: 'Werterhalt',
    description: 'Der Originallack bleibt erhalten, was den Wiederverkaufswert Ihres Fahrzeugs schützt.'
  }, {
    icon: <Clock className="w-6 h-6 text-primary" />,
    title: 'Schnelligkeit',
    description: 'Die meisten Reparaturen erledigen wir innerhalb weniger Stunden, oft direkt vor Ort.'
  }, {
    icon: <Euro className="w-6 h-6 text-primary" />,
    title: 'Kosteneffizient',
    description: 'Sparen Sie bis zu 70% gegenüber herkömmlichen Lackierverfahren.'
  }, {
    icon: <Leaf className="w-6 h-6 text-primary" />,
    title: 'Umweltfreundlich',
    description: 'Keine Spachtelmasse, kein Schleifstaub und keine giftigen Lacklösungsmittel.'
  }];
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.HERO_REPAIR_2} alt="Professionelle Dellenreparatur" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              <span>Smart Repair Spezialist 201</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 text-foreground">
              {companyInfo.name}
              <span className="block text-primary">Präzision in Perfektion.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed max-w-2xl">
              {companyInfo.tagline}. Wir entfernen Dellen und Beulen ohne Lackieren – sanft, schnell und kostengünstig direkt in München.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full px-8 shadow-lg shadow-primary/20">
                <Link to={ROUTE_PATHS.CONTACT}>Termin vereinbaren</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 backdrop-blur-sm">
                <Link to={ROUTE_PATHS.SERVICES}>Unsere Leistungen</Link>
              </Button>
            </div>

            <div className="mt-12 flex items-center gap-6">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-background bg-muted flex items-center justify-center overflow-hidden">
                    <img src={`https://i.pravatar.cc/150?u=${i}`} alt="User" />
                  </div>)}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-chart-4 text-chart-4" />)}
                </div>
                <p className="text-sm text-muted-foreground font-medium">Über 500+ zufriedene Kunden</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="p-6 rounded-2xl bg-background border border-border/50 hover:border-primary/30 transition-colors group">
                <div className="mb-4 p-3 rounded-xl bg-primary/5 w-fit group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 italic text-primary/80">Meisterhafte Handwerkskunst</h2>
              <h3 className="text-4xl md:text-5xl font-bold mb-6">Unsere Kernkompetenzen</h3>
              <p className="text-lg text-muted-foreground">
                Von kleinen Parkremplern bis hin zu großflächigen Hagelschäden – wir beherrschen jede Nuance der sanften Instandsetzung.
              </p>
            </div>
            <Button asChild variant="ghost" className="group">
              <Link to={ROUTE_PATHS.SERVICES} className="flex items-center gap-2">
                Alle Leistungen ansehen <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.map(service => <ServiceCard key={service.id} service={service} />)}
          </div>
        </div>
      </section>

      {/* Before/After Showcase */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Das Ergebnis zählt</h2>
            <p className="text-lg text-muted-foreground">
              Überzeugen Sie sich selbst von der Qualität unserer Arbeit. Hier sehen Sie echte Beispiele von Fahrzeugen, die wir wieder in ihren Neuzustand versetzt haben.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <BeforeAfterCard title="Komplexer Parkschaden" beforeImage={IMAGES.BEFORE_AFTER_1} afterImage={IMAGES.BEFORE_AFTER_2} description="Tiefe Delle an der hinteren Tür eines Audi A4. Instandsetzung ohne Lackieren in nur 4 Stunden." />
            <BeforeAfterCard title="Hagelschaden Motorhaube" beforeImage={IMAGES.BEFORE_AFTER_6} afterImage={IMAGES.BEFORE_AFTER_7} description="Über 40 Einschläge auf einer Aluminium-Motorhaube. Rückstandslos entfernt." />
          </div>
          
          <div className="mt-12 text-center">
            <Button asChild size="lg" variant="outline" className="rounded-full">
              
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center">Was unsere Kunden sagen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredTestimonials.map(testimonial => <TestimonialCard key={testimonial.id} testimonial={testimonial} />)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="relative rounded-[2rem] overflow-hidden bg-primary p-8 md:p-16 text-primary-foreground">
            <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
               <img src={IMAGES.TOOLS_WORKSHOP_5} alt="Workshop" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">Bereit für ein makelloses Fahrzeug?</h2>
              <p className="text-xl opacity-90 mb-10 leading-relaxed">
                Senden Sie uns einfach ein Foto Ihres Schadens per WhatsApp oder vereinbaren Sie direkt einen Besichtigungstermin in unserer Werkstatt.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" variant="secondary" className="rounded-full px-8 font-bold">
                  <Link to={ROUTE_PATHS.CONTACT}>Jetzt anfragen</Link>
                </Button>
                <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-primary-foreground/30 hover:bg-primary-foreground/10 transition-colors font-semibold">
                  WhatsApp Nachricht
                </a>
              </div>
              
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['Kostenlose Erstberatung', 'Zertifizierter Fachbetrieb', 'Direktabrechnung mit Versicherung', 'Garantie auf alle Arbeiten'].map((item, idx) => <li key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-chart-4" />
                    <span className="text-sm font-medium">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;